import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import ManagementHeader from './ManagementHeader';
import MgField from './MgField';
import { fetchFactoryDetailForManagement, updateFactory, deleteFactory } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './management.css';

const DASH = '—';
const STATUS_BADGE = { '정상': 'success', '경고': 'warning', '이상': 'error' };
const rateClass = (rate) => {
    if (rate >= 85) return 'high';
    if (rate >= 70) return 'medium';
    return 'low';
};

function FactoryDetailPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { factoryId } = useParams();
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);

    // URL 쿼리 파라미터에서 edit 모드 확인
    useEffect(() => {
        const editMode = searchParams.get('edit');
        if (editMode === 'true') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [searchParams]);

    // API에서 공장 데이터 가져오기
    const [formData, setFormData] = useState({
        id: factoryId,
        name: '',
        code: '',
        location: '',
        address: '',
        manager: '',
        contact: '',
        email: '',
        totalEquipment: 0,
        runningEquipment: 0,
        idleEquipment: 0,
        stoppedEquipment: 0,
        temperature: '-',
        humidity: '-',
        operationRate: 0,
        status: '정상',
        lastInspection: '-',
        establishedDate: '-',
        description: ''
    });

    useEffect(() => {
        const loadFactoryDetail = async () => {
            try {
                setLoading(true);
                const data = await fetchFactoryDetailForManagement(factoryId);


                // API 데이터를 formData 형식으로 변환
                const transformedData = {
                    id: data.id,
                    name: data.factoryName || '',
                    code: data.factoryCode || '',
                    location: data.address || '',
                    address: data.address || '',
                    manager: data.managerName || '',
                    contact: data.managerPhone || '',
                    email: data.managerEmail || '',
                    totalEquipment: data.totalEquipment || 0,
                    runningEquipment: data.runningEquipment || 0,
                    idleEquipment: data.idleEquipment || 0,
                    stoppedEquipment: data.stoppedEquipment || 0,
                    temperature: data.temperature ? `${data.temperature}°C` : '-',
                    humidity: data.humidity ? `${data.humidity}%` : '-',
                    operationRate: data.operationRate != null ? data.operationRate : null, // 미측정은 null → —
                    status: data.status === 'NORMAL' ? '정상' : data.status === 'WARNING' ? '경고' : '이상',
                    lastInspection: data.lastInspectionDate || '-',
                    establishedDate: data.createdAt ? data.createdAt.split('T')[0] : '-',
                    description: data.description || ''
                };

                setFormData(transformedData);
                setOriginalData(transformedData);
            } catch (err) {
                console.error('공장 상세정보 조회 실패:', err);
                alert(t('pages.management.factoryDetail.alertLoadFail'));
            } finally {
                setLoading(false);
            }
        };

        if (factoryId) {
            loadFactoryDetail();
        }
    }, [factoryId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = () => {
        // URL에 ?edit=true 쿼리 파라미터 추가
        navigate(`/factory/${factoryId}?edit=true`, { replace: true });
    };

    const handleCancel = () => {
        if (isEditing) {
            // URL에서 ?edit=true 제거
            navigate(`/factory/${factoryId}`, { replace: true });
            // 원래 데이터로 복원
            if (originalData) {
                setFormData(originalData);
            }
        } else {
            navigate('/factory-list');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            // API 형식에 맞게 데이터 변환
            const requestData = {
                factoryCode: formData.code,
                factoryName: formData.name,
                address: formData.address,
                latitude: null,
                longitude: null,
                managerName: formData.manager,
                managerPhone: formData.contact,
                managerEmail: formData.email,
                status: formData.status === '정상' ? 'NORMAL' : formData.status === '경고' ? 'WARNING' : 'ERROR',
                description: formData.description,
                lastInspectionDate: formData.lastInspection !== '-' ? formData.lastInspection : null
            };


            await updateFactory(factoryId, requestData);

            alert(t('pages.management.factoryDetail.alertUpdateSuccess'));
            // 수정 모드 해제 및 데이터 다시 로드
            navigate(`/factory/${factoryId}`, { replace: true });

            // 페이지 새로고침하여 최신 데이터 로드
            window.location.reload();
        } catch (err) {
            console.error('공장 정보 수정 실패:', err);
            alert(t('pages.management.factoryDetail.alertUpdateFail'));
        }
    };

    const handleDelete = async () => {
        if (window.confirm(t('pages.management.factoryDetail.confirmDelete'))) {
            try {

                await deleteFactory(factoryId);

                alert(t('pages.management.factoryDetail.alertDeleteSuccess'));
                navigate('/factory-list');
            } catch (err) {
                console.error('공장 삭제 실패:', err);
                alert(t('pages.management.factoryDetail.alertDeleteFail'));
            }
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case '정상':
                return t('pages.management.factoryDetail.status.normal');
            case '경고':
                return t('pages.management.factoryDetail.status.warning');
            case '이상':
                return t('pages.management.factoryDetail.status.error');
            default:
                return status;
        }
    };

    const L = (key) => t(`pages.management.factoryDetail.label.${key}`);
    const count = (n) => t('pages.management.factoryDetail.unit.count', { n });
    /* 조회 모드 값: 비어 있거나 '-' 이면 — */
    const show = (v) => (v == null || v === '' || v === '-' ? DASH : v);
    const field = (name, type = 'text') => (isEditing ? (
        <input type={type} name={name} className="mg-input" value={formData[name]} onChange={handleInputChange} required />
    ) : (
        <div className="mg-value">{show(formData[name])}</div>
    ));

    if (loading) {
        return (
            <MainLayout>
                <div className="mg-page">
                    <div className="mg-loading">{t('pages.management.factoryDetail.loading')}</div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader
                    backTo="/factory-list"
                    crumbs={[
                        { label: t('sidebar.management.factoryList'), to: '/factory-list' },
                        { label: t('pages.management.factoryDetail.title') }
                    ]}
                >
                    {!isEditing ? (
                        <>
                            <button type="button" className="mg-btn danger" onClick={handleDelete}>
                                <span className="material-symbols-outlined">delete</span>
                                {t('pages.management.factoryDetail.btn.delete')}
                            </button>
                            <button type="button" className="mg-btn primary" onClick={handleEdit}>
                                <span className="material-symbols-outlined">edit</span>
                                {t('pages.management.factoryDetail.btn.edit')}
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="mg-btn secondary" onClick={handleCancel}>
                                <span className="material-symbols-outlined">close</span>
                                {t('pages.management.factoryDetail.btn.cancel')}
                            </button>
                            <button type="button" className="mg-btn primary" onClick={handleSave}>
                                <span className="material-symbols-outlined">check</span>
                                {t('pages.management.factoryDetail.btn.save')}
                            </button>
                        </>
                    )}
                </ManagementHeader>

                {/* 프로필 카드 */}
                <div className="mg-panel mg-profile">
                    <span className="mg-profile-icon"><span className="material-symbols-outlined">factory</span></span>
                    <div className="mg-profile-info">
                        <h2 className="mg-profile-name">{show(formData.name)}</h2>
                        <p className="mg-profile-sub">{show(formData.code)}</p>
                        <span className={`mg-badge ${STATUS_BADGE[formData.status] || 'neutral'}`}>{getStatusLabel(formData.status)}</span>
                    </div>
                    <div className="mg-stats">
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('totalEquipmentShort')}</span>
                            <span className="mg-stat-value">{count(formData.totalEquipment)}</span>
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('operationRate')}</span>
                            {formData.operationRate == null ? (
                                <span className="mg-stat-value">{DASH}</span>
                            ) : (
                                <span className="mg-rate">
                                    <span className="mg-rate-track">
                                        <span className={`mg-rate-bar ${rateClass(formData.operationRate)}`} style={{ width: `${Math.min(100, formData.operationRate)}%` }} />
                                    </span>
                                    <span className="mg-stat-value">{formData.operationRate}%</span>
                                </span>
                            )}
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('establishedDate')}</span>
                            <span className="mg-stat-value">{show(formData.establishedDate)}</span>
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('lastInspection')}</span>
                            <span className="mg-stat-value">{show(formData.lastInspection)}</span>
                        </div>
                    </div>
                </div>

                <form className={`mg-form ${isEditing ? 'editing' : ''}`} onSubmit={handleSave}>
                    {/* 기본 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.factoryDetail.section.basic')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('name')}>{field('name')}</MgField>
                            <MgField label={L('code')}>{field('code')}</MgField>
                            <MgField label={L('location')}>{field('location')}</MgField>
                            <MgField label={L('address')}>{field('address')}</MgField>
                        </div>
                    </div>

                    {/* 담당자 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.factoryDetail.section.manager')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('managerName')}>{field('manager')}</MgField>
                            <MgField label={L('contact')}>{field('contact', 'tel')}</MgField>
                            <MgField label={L('email')}>{field('email', 'email')}</MgField>
                        </div>
                    </div>

                    {/* 운영 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.factoryDetail.section.operation')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('totalEquipment')}>
                                {isEditing ? (
                                    <input type="number" name="totalEquipment" className="mg-input" value={formData.totalEquipment} onChange={handleInputChange} required min="0" />
                                ) : (
                                    <div className="mg-value">{count(formData.totalEquipment)}</div>
                                )}
                            </MgField>
                            <MgField label={L('running')}><div className="mg-value running">{count(formData.runningEquipment)}</div></MgField>
                            <MgField label={L('idle')}><div className="mg-value idle">{count(formData.idleEquipment)}</div></MgField>
                            <MgField label={L('stopped')}><div className="mg-value stopped">{count(formData.stoppedEquipment)}</div></MgField>
                            <MgField label={L('status')}>
                                {isEditing ? (
                                    <select name="status" className="mg-select" value={formData.status} onChange={handleInputChange} required>
                                        <option value="정상">{t('pages.management.factoryDetail.status.normal')}</option>
                                        <option value="경고">{t('pages.management.factoryDetail.status.warning')}</option>
                                        <option value="이상">{t('pages.management.factoryDetail.status.error')}</option>
                                    </select>
                                ) : (
                                    <div className="mg-value">{getStatusLabel(formData.status)}</div>
                                )}
                            </MgField>
                            <MgField label={L('description')} full>
                                {isEditing ? (
                                    <textarea name="description" className="mg-textarea" value={formData.description} onChange={handleInputChange} rows="4" />
                                ) : (
                                    <div className="mg-value">{show(formData.description)}</div>
                                )}
                            </MgField>
                        </div>
                    </div>

                    {/* 환경 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.factoryDetail.section.environment')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('temperature')}><div className="mg-value">{show(formData.temperature)}</div></MgField>
                            <MgField label={L('humidity')}><div className="mg-value">{show(formData.humidity)}</div></MgField>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="mg-form-actions">
                            <button type="button" className="mg-btn secondary" onClick={handleCancel}>
                                {t('pages.management.factoryDetail.btn.cancel')}
                            </button>
                            <button type="submit" className="mg-btn primary">
                                {t('pages.management.factoryDetail.btn.save')}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </MainLayout>
    );
}

export default FactoryDetailPage;
